import { useTranslation } from "react-i18next";

import { CHECKLIST_TYPES } from "models";

import type { TFunction } from "i18next";
import type { Checklist } from "models";
import type { CSSProperties } from "react";
import type { DropdownItemProps } from "semantic-ui-react";

type ChecklistGroup = {
  name: string;
  checklists: Checklist[];
  infoText?: string;
};

export interface GroupedChecklistsDropdownOptions extends DropdownItemProps {
  key: number | string;
  text: string;
  value: number | string;
  disabled?: boolean;
  infoText?: string;
}

const groupStyle: CSSProperties = { fontStyle: "italic", fontWeight: "normal", color: "color: #000000DE" };

const hasSkipUpdateAppStatusSettingDisabled = (checklists: Checklist[], type: CHECKLIST_TYPES) =>
  !checklists.find(c => c.checklist_type === type)?.template?.skip_update_appointment_status;

const getGroupedChecklists = (t: TFunction, checklists: Checklist[]) => {
  const groups = new Map<CHECKLIST_TYPES, ChecklistGroup>([]);
  groups.set(CHECKLIST_TYPES.Regular, { name: t("v8_regular", "Regular"), checklists: [] });
  groups.set(CHECKLIST_TYPES.Warranty, { name: t("v8_warranty", "Warranty"), checklists: [] });
  groups.set(CHECKLIST_TYPES.Info, {
    name: t("v8_info", "Info"),
    checklists: [],
    ...(hasSkipUpdateAppStatusSettingDisabled(checklists, CHECKLIST_TYPES.Info) && {
      infoText: t("v8_only_assignable_to_wo_with_info_status", "Only assignable to WO with Info Status")
    })
  });
  groups.set(CHECKLIST_TYPES.Aanname, {
    name: t("v8_onboarding", "Onboarding"),
    checklists: [],
    ...(hasSkipUpdateAppStatusSettingDisabled(checklists, CHECKLIST_TYPES.Aanname) && {
      infoText: t("v8_only_assignable_to_wo_with_new_car_status", "Only assignable to WO with New Car status")
    })
  });
  groups.set(CHECKLIST_TYPES.TestDrive, { name: t("v8_test_drive", "Test Drive"), checklists: [] });

  return groups;
};

const getGroupedChecklistsOptions = (checklists: Checklist[], groupedChecklists: Map<CHECKLIST_TYPES, ChecklistGroup>) => {
  const checklistOptions: GroupedChecklistsDropdownOptions[] = [];

  checklists.forEach(c => groupedChecklists.get(c.checklist_type)?.checklists.push(c));

  groupedChecklists.forEach((group, type) => {
    if (!group.checklists.length) return;

    checklistOptions.push({
      key: `grouped-${type}`,
      value: `grouped-${type}`,
      text: group.name,
      disabled: true,
      infoText: group.infoText,
      children: (
        <div>
          <span>{group.name} </span>
          {group.infoText && <span style={groupStyle}> - {group.infoText}</span>}
        </div>
      )
    });
    group.checklists.sort((a, b) => a.name.localeCompare(b.name));
    group.checklists.forEach(c => checklistOptions.push({ key: c.id, value: c.id, text: c.name, disabled: false, infoText: group.infoText }));
  });

  return checklistOptions;
};

type UseChecklistDropdownOptionsReturn = {
  groupedChecklistsDropdownOptions: GroupedChecklistsDropdownOptions[];
};

export const useChecklistDropdownOptions = (checklists?: Checklist[]): UseChecklistDropdownOptionsReturn => {
  const { t } = useTranslation();
  if (!checklists) return { groupedChecklistsDropdownOptions: [] };

  const groupedChecklists = getGroupedChecklists(t, checklists);
  return { groupedChecklistsDropdownOptions: getGroupedChecklistsOptions(checklists, groupedChecklists) };
};
